import { DatePipe, DecimalPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, ViewChild, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  BadgeComponent, ButtonComponent, CardComponent, ConfirmDialogComponent,
  ContentTypeBadgeComponent, EmptyStateComponent, SpinnerComponent,
  TabSetComponent, ToastService, type Tab,
} from '@echospaces-web-monorepo/es-shared-ui';

import { AddLanguageModalComponent } from './add-language-modal.component';
import { ContentService, type ContentFile } from './content.service';
import { EditContentModalComponent } from './edit-content-modal.component';

/**
 * Detail view for a single content object — header (title, type,
 * description, edit/delete) + a tab-set of language variants. Each tab
 * shows the latest file for that language with size / mime / version /
 * upload timestamp and a "Replace" action. The "+ Add language" tab
 * opens the AddLanguageModal pre-filled with no language; "Replace"
 * opens the same modal pre-filled with the active tab's language.
 */
@Component({
  selector: 'es-content-detail-page',
  imports: [
    DatePipe, DecimalPipe,
    BadgeComponent, ButtonComponent, CardComponent, ConfirmDialogComponent,
    ContentTypeBadgeComponent, EmptyStateComponent, SpinnerComponent, TabSetComponent,
    EditContentModalComponent, AddLanguageModalComponent,
  ],
  template: `
    <div class="max-w-4xl mx-auto">
      <!-- Top bar -->
      <button
        class="text-sm text-[var(--text-secondary)] hover:text-[var(--text)] cursor-pointer mb-4"
        (click)="back()"
      >← Back to content library</button>

      @if (service.detailLoading()) {
        <div class="flex justify-center py-12">
          <es-spinner label="Loading…"></es-spinner>
        </div>
      } @else if (service.selectedContent(); as content) {
        <!-- Header -->
        <div class="flex items-start justify-between mb-6">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-2">
              <es-content-type-badge [type]="content.type"></es-content-type-badge>
              @if (content.spaceId) {
                <es-badge label="space-scoped" variant="secondary"></es-badge>
              }
            </div>
            <h1 class="text-2xl font-bold font-exo">{{ content.title }}</h1>
            @if (content.description) {
              <p class="text-sm text-[var(--text-secondary)] mt-1">{{ content.description }}</p>
            }
          </div>
          <div class="flex items-center gap-2 shrink-0 ml-4">
            <es-button variant="secondary" size="sm" (clicked)="editOpen.set(true)">Edit</es-button>
            <es-button variant="danger" size="sm" (clicked)="deleteOpen.set(true)">Delete</es-button>
          </div>
        </div>

        <!-- Language tabs -->
        @if (languageTabs().length === 0) {
          <es-empty-state
            title="No files yet"
            description="Upload the first file to attach a binary to this content."
            icon="📤"
            actionLabel="Upload file"
            (action)="openAddLanguage(null)"
          ></es-empty-state>
        } @else {
          <es-tab-set [tabs]="languageTabs()" (tabChanged)="onTabChanged($event)">
            @if (activeTabId() === '__add') {
              <es-empty-state
                title="Add a language variant"
                description="Upload a file for a new locale code (de, en, fr, …)."
                icon="🌐"
                actionLabel="Upload file"
                (action)="openAddLanguage(null)"
              ></es-empty-state>
            } @else if (activeFile(); as file) {
              <es-card>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="text-[var(--text-secondary)]">Language</span>
                    <p class="font-medium">{{ file.language }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Version</span>
                    <p class="font-medium">v{{ file.version }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">MIME type</span>
                    <p class="font-medium">{{ file.mimeType }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Size</span>
                    <p class="font-medium">{{ formatBytes(file.fileSize) | number:'1.0-2' }} MB</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Uploaded</span>
                    <p class="font-medium">{{ file.createdDate | date:'medium' }}</p>
                  </div>
                  <div>
                    <span class="text-[var(--text-secondary)]">Updated</span>
                    <p class="font-medium">{{ file.updatedDate | date:'medium' }}</p>
                  </div>
                </div>

                <div class="flex justify-end gap-2 mt-4 pt-4 border-t border-[var(--border)]">
                  <es-button variant="secondary" size="sm" (clicked)="openAddLanguage(file.language)">
                    Replace file
                  </es-button>
                </div>
              </es-card>
            }
          </es-tab-set>
        }
      } @else {
        <es-empty-state
          title="Content not found"
          description="The content you tried to open does not exist or you lack access."
          icon="🔍"
        ></es-empty-state>
      }

      <!-- Modals -->
      <es-edit-content-modal
        #editModal
        [open]="editOpen()"
        [content]="service.selectedContent()"
        (closed)="editOpen.set(false)"
        (submitted)="onEditSubmit($event)"
      ></es-edit-content-modal>

      <es-add-language-modal
        #addLangModal
        [open]="addLangOpen()"
        [initialLanguage]="addLangInitial()"
        (closed)="addLangOpen.set(false)"
        (submitted)="onAddLangSubmit($event)"
      ></es-add-language-modal>

      <es-confirm-dialog
        [open]="deleteOpen()"
        title="Delete content?"
        message="This deletes the content row and every uploaded file from object storage. Cannot be undone."
        confirmLabel="Delete"
        [destructive]="true"
        (confirmed)="confirmDelete()"
        (cancelled)="deleteOpen.set(false)"
      ></es-confirm-dialog>
    </div>
  `,
})
export class ContentDetailPageComponent implements OnInit {
  @ViewChild('editModal') editModalRef?: EditContentModalComponent;
  @ViewChild('addLangModal') addLangModalRef?: AddLanguageModalComponent;

  readonly service = inject(ContentService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  readonly editOpen = signal(false);
  readonly addLangOpen = signal(false);
  readonly deleteOpen = signal(false);
  readonly addLangInitial = signal<string | null>(null);
  readonly activeTabId = signal<string>('');

  /** Tabs derived from `service.languages()` + a virtual `+` tab to add a new variant. */
  readonly languageTabs = computed<Tab[]>(() => {
    const langs = this.service.languages();
    const tabs: Tab[] = langs.map((lang) => ({ id: lang, label: lang.toUpperCase() }));
    if (langs.length > 0) tabs.push({ id: '__add', label: '+ Add language', icon: '🌐' });
    return tabs;
  });

  readonly activeFile = computed<ContentFile | undefined>(() =>
    this.service.latestPerLanguage().get(this.activeTabId()));

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.service.loadContent(id).then(() => {
        const langs = this.service.languages();
        if (langs.length > 0 && !this.activeTabId()) this.activeTabId.set(langs[0]);
      });
    }
  }

  onTabChanged(tabId: string): void {
    this.activeTabId.set(tabId);
  }

  openAddLanguage(initial: string | null): void {
    this.addLangInitial.set(initial);
    this.addLangOpen.set(true);
  }

  async onEditSubmit({ dto, file }: { dto: { title?: string; description?: string }; file?: File }) {
    const content = this.service.selectedContent();
    if (!content) return;
    try {
      await this.service.updateContent(content.id, dto, file);
      this.editModalRef?.finishSubmit();
      this.toast.success('Content updated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update';
      this.editModalRef?.finishSubmit(message);
    }
  }

  async onAddLangSubmit({ language, file }: { language: string; file: File }) {
    const content = this.service.selectedContent();
    if (!content) return;
    try {
      await this.service.addLanguageVariant(content.id, language, file);
      this.addLangModalRef?.finishSubmit();
      this.toast.success(`Uploaded ${language}`);
      this.activeTabId.set(language);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      this.addLangModalRef?.finishSubmit(message);
    }
  }

  async confirmDelete(): Promise<void> {
    const content = this.service.selectedContent();
    if (!content) return;
    this.deleteOpen.set(false);
    try {
      await this.service.deleteContent(content.id);
      this.toast.success('Content deleted');
      this.router.navigate(['/content']);
    } catch {
      this.toast.error('Failed to delete');
    }
  }

  back(): void {
    this.router.navigate(['/content']);
  }

  formatBytes(bytes: number): number {
    return bytes / (1024 * 1024);
  }
}
